import is from '@sindresorhus/is';
import { useMemo } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileComponent } from '~/shared/data-contract/job-profile';
import { jobProfileComponentSchema } from '~/shared/data-contract/job-profile';
import type { VersionSelect } from '~/ui/domain/version-select/VersionSelect';

import type { ChangeProposalCompareProfile } from '../../data-contract/chat';
import { useChangeProposalCompare } from '../api/use-change-proposal-compare';
import { DraftBadge } from '../components/DraftBadge';
import { JSA_COMPARE_PROPOSAL_VERSION } from '../url-tile-payloads';

export const changeProposalCompareProfileToJobProfileComponent = (
    profile: ChangeProposalCompareProfile,
): JobProfileComponent =>
    jobProfileComponentSchema.parse({
        id: profile.id,
        competencies: profile.competencies.map((competency) => ({
            id: competency.id,
            title: competency.title,
            titles: [],
        })),
        language: profile.language,
        sectionTags: profile.sectionTags,
        skills: profile.skills.map((skill) => ({
            id: skill.id,
            ...(is.nullOrUndefined(skill.competencyId) ? {} : { competencyId: skill.competencyId }),
            level: skill.level,
            // The compare payload may omit the skill type; hard is the least
            // surprising rendering fallback for an unknown type.
            skillType: skill.type ?? 'hard',
            title: skill.title,
            titles: [],
        })),
        structured: profile.structured,
        title: profile.title,
        translations: profile.translations.map((translation) => ({
            language: translation.language,
            structured: translation.structured,
            title: translation.title,
        })),
        updatedAt: profile.updatedAt,
    });

// Compare-tile view of a chat change proposal: the frozen proposed profile in
// JobProfileComponent shape plus the synthetic version-select entry it appears as.
export const useProposalCompare = (
    proposalBlockId: string | undefined,
): {
    readonly hasProposalError: boolean;
    readonly proposalVersion: VersionSelect | null;
    readonly proposedProfile: JobProfileComponent | undefined;
} => {
    const translate = useTranslate();
    const isProposalMode = is.nonEmptyString(proposalBlockId);

    const { data, isError } = useChangeProposalCompare({
        blockId: proposalBlockId ?? '',
        enabled: isProposalMode,
    });

    const proposedProfile = useMemo(
        () => (is.undefined(data) ? undefined : changeProposalCompareProfileToJobProfileComponent(data.proposed)),
        [data],
    );

    if (!isProposalMode) {
        return { hasProposalError: false, proposalVersion: null, proposedProfile: undefined };
    }

    return {
        hasProposalError: isError,
        proposalVersion: {
            id: JSA_COMPARE_PROPOSAL_VERSION,
            author: '',
            badge: <DraftBadge />,
            title: translate('pages.job-architecture-v2.job-profile-compare-proposal'),
        },
        proposedProfile,
    };
};
